import moment from 'moment';

import {
  EngagementBillingCadence,
  EngagementPacingType,
  type MakeEngagementOfferInput,
} from '@/__generated__/graphql';
import DatePicker from '@/componentsv2/DatePicker';
import InfoTip from '@/componentsv2/InfoTip';
import Input from '@/componentsv2/Input';
import Radio from '@/componentsv2/RadioButton/Radio';

import { TERMS } from './termCopy';

/** `MakeEngagementOfferInput.offerScopeDescription` is capped server-side. */
const SCOPE_MAX = 2000;

/** `EngagementPacingType.None` is a real choice, so pacing is never null here. */
export interface OfferTermsValue {
  offerScopeDescription: string;
  /** ISO strings; the api's `Datetime` scalar. */
  startDate: string | null;
  endDate: string | null;
  billingCadence: EngagementBillingCadence;
  pacingType: EngagementPacingType;
  /** Hours per pacing period, as typed. Ignored when `pacingType` is `none`. */
  pacingValue: string;
}

export interface OfferTermsErrors {
  scope?: string;
  dates?: string;
  pacing?: string;
}

/**
 * The expert's scope prefills from the customer's request — they are answering
 * it, and starting from a blank box invites a thinner answer than the ask.
 */
export function initialOfferTerms(requestScope: string | null): OfferTermsValue {
  return {
    offerScopeDescription: requestScope ?? '',
    startDate: null,
    endDate: null,
    billingCadence: EngagementBillingCadence.Monthly,
    pacingType: EngagementPacingType.None,
    pacingValue: '',
  };
}

/**
 * Client-side twins of the server's `engagement.scope_too_long` and
 * `engagement.invalid_dates`, so the expert finds out before the round-trip.
 * The server remains the authority; these only save a trip.
 */
export function offerTermsErrors(value: OfferTermsValue): OfferTermsErrors {
  const errors: OfferTermsErrors = {};

  if (value.offerScopeDescription.length > SCOPE_MAX) {
    errors.scope = `Keep the scope to ${SCOPE_MAX} characters or fewer.`;
  }
  if (
    value.startDate !== null &&
    value.endDate !== null &&
    moment(value.endDate).isBefore(moment(value.startDate))
  ) {
    errors.dates = 'The end date can’t be before the start date.';
  }
  if (value.pacingType !== EngagementPacingType.None) {
    const paced = Number.parseFloat(value.pacingValue);
    if (!Number.isFinite(paced) || paced <= 0) {
      errors.pacing = 'Enter the hours allowed in each period.';
    }
  }
  return errors;
}

/**
 * The optional half of `MakeEngagementOfferInput`. Keys are omitted rather than
 * sent as null so an untouched field stays untouched server-side.
 */
export function offerTermsInput(value: OfferTermsValue): Partial<MakeEngagementOfferInput> {
  const scope = value.offerScopeDescription.trim();
  return {
    ...(scope ? { offerScopeDescription: scope } : {}),
    ...(value.startDate ? { startDate: value.startDate } : {}),
    ...(value.endDate ? { endDate: value.endDate } : {}),
    billingCadence: value.billingCadence,
    pacingType: value.pacingType,
    ...(value.pacingType !== EngagementPacingType.None && value.pacingValue
      ? { pacingValue: Number.parseFloat(value.pacingValue).toFixed(2) }
      : {}),
  };
}

interface OfferTermsProps {
  value: OfferTermsValue;
  errors: OfferTermsErrors;
  /** True once the expert has tried to submit; gates error display. */
  showErrors: boolean;
  onChange: (next: OfferTermsValue) => void;
}

const CADENCES: { value: EngagementBillingCadence; label: string }[] = [
  { value: EngagementBillingCadence.Monthly, label: 'Monthly' },
  { value: EngagementBillingCadence.Biweekly, label: 'Every two weeks' },
];

const PACING: { value: EngagementPacingType; label: string }[] = [
  { value: EngagementPacingType.None, label: 'No cap per period' },
  { value: EngagementPacingType.Weekly, label: 'Per week' },
  { value: EngagementPacingType.Monthly, label: 'Per month' },
];

/**
 * Scope, dates, cadence and pacing — the write-side inputs ENG-5604 (G6) added
 * to `makeEngagementOffer`.
 *
 * FE2a removed these sections rather than disabling them, because the mutation
 * had nowhere to put the values (plan §9 Q4: remove, return additively). This
 * is that additive return.
 *
 * Dates are presentational by api contract — never enforced against the work —
 * so the copy says so rather than implying a hard boundary.
 */
export default function OfferTerms({
  value,
  errors,
  showErrors,
  onChange,
}: OfferTermsProps): JSX.Element {
  function set<K extends keyof OfferTermsValue>(key: K, next: OfferTermsValue[K]) {
    onChange({ ...value, [key]: next });
  }

  return (
    <div className="flex flex-col gap-24">
      <Input
        label="Scope of work"
        multiline
        rows={4}
        value={value.offerScopeDescription}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          set('offerScopeDescription', e.target.value)
        }
        error={showErrors && errors.scope !== undefined}
        helperText={
          showErrors && errors.scope
            ? errors.scope
            : 'What you’ll deliver. Prefilled from the customer’s request — edit it to match what you’re committing to.'
        }
      />

      <fieldset className="flex flex-col gap-8 border-[0] p-[0]">
        <legend className="text-on-surface-muted subtitle-2">
          Intended dates (indicative — not enforced against your hours)
        </legend>
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
          <DatePicker
            label="Start"
            includeFutureDatePick
            value={value.startDate ? moment(value.startDate) : null}
            onChange={(v) => set('startDate', asIso(v))}
          />
          <DatePicker
            label="End"
            includeFutureDatePick
            value={value.endDate ? moment(value.endDate) : null}
            onChange={(v) => set('endDate', asIso(v))}
          />
        </div>
        {showErrors && errors.dates ? <p className="text-red-600 body-2">{errors.dates}</p> : null}
      </fieldset>

      <fieldset className="flex flex-col gap-4 border-[0] p-[0]">
        <legend className="flex items-center gap-4 text-on-surface-muted subtitle-2">
          {TERMS.billingCadence.label}
          <InfoTip term={TERMS.billingCadence.label} definition={TERMS.billingCadence.definition} />
        </legend>
        <div className="flex flex-wrap gap-16">
          {CADENCES.map((option) => (
            <Radio
              key={option.value}
              label={option.label}
              checked={value.billingCadence === option.value}
              onChange={() => set('billingCadence', option.value)}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-4 border-[0] p-[0]">
        <legend className="flex items-center gap-4 text-on-surface-muted subtitle-2">
          Pace the not-to-exceed hours
          <InfoTip term={TERMS.ntePacing.label} definition={TERMS.ntePacing.definition} />
        </legend>
        <div className="flex flex-wrap gap-16">
          {PACING.map((option) => (
            <Radio
              key={option.value}
              label={option.label}
              checked={value.pacingType === option.value}
              onChange={() => set('pacingType', option.value)}
            />
          ))}
        </div>
        {value.pacingType !== EngagementPacingType.None ? (
          <Input
            label="Hours per period"
            value={value.pacingValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set('pacingValue', e.target.value)
            }
            error={showErrors && errors.pacing !== undefined}
            helperText={showErrors && errors.pacing ? errors.pacing : ''}
          />
        ) : null}
      </fieldset>
    </div>
  );
}

/** The picker hands back a moment (or a current-checkbox shape we don't use). */
function asIso(value: unknown): string | null {
  return moment.isMoment(value) ? value.toISOString() : null;
}
